/**
 * MixerEngine — volume, pan, mute, solo, master, medidores e balanceamento.
 *
 * "MELHORAR MIX AUTOMATICAMENTE" é um ajuste automático SIMPLES e diz isso na
 * UI: analisa níveis, aplica um preset conservador e reporta o que fez.
 * Não fingimos masterização profissional.
 */
import type { AudioEngine } from './AudioEngine.js';
import type { FxConfig, MeterReading, MixSettings, Track } from '../types.js';
import { applyPreset, defaultFx } from './fx.js';
import { autoGain, loudnessLKFS, peak, rms } from './analysis.js';
import { clamp, dbToGain, gainToDb } from '../utils/misc.js';

export interface MixerState {
  tracks: Track[];
  mix: MixSettings;
  instrumentalData?: { left: Float32Array; right: Float32Array } | null;
  vocalData?: Record<string, Float32Array>;
  sampleRate: number;
}

export interface AutoBalanceReport {
  applied: { trackId: string; gainDb: number; fromDb: number; toDb: number }[];
  instrumentalDb: number | null;
  vocalDb: number | null;
  balanceDb: number | null;
  message: string;
  clipping: string[];
}

export interface AutoImproveReport {
  changes: string[];
  fx: Record<string, FxConfig>;
  gains: Record<string, number>;
  master: { volume: number; limiter: boolean };
  message: string;
}

export class MixerEngine {
  constructor(private audio: AudioEngine) {}

  applyMaster(mix: MixSettings) {
    this.audio.setMasterVolume(mix.masterVolume);
    this.audio.setMasterLimiter(true, mix.masterLimitDb);
    this.audio.setInstrumentalVolume(mix.instrumentalVolume);
    this.audio.setInstrumentalPan(mix.instrumentalPan);
    this.audio.setInstrumentalMuted(mix.instrumentalMuted);
  }

  /** Resolve mute/solo no padrão DAW (solo isola; instrumental também entra). */
  resolveAudibility(state: MixerState): Record<string, boolean> {
    const anySolo = state.tracks.some((t) => t.solo) || state.mix.instrumentalSolo;
    const out: Record<string, boolean> = {};
    for (const t of state.tracks) {
      out[t.id] = t.muted ? false : anySolo ? t.solo : true;
    }
    out.__instrumental__ = state.mix.instrumentalMuted
      ? false
      : anySolo
      ? state.mix.instrumentalSolo
      : true;
    return out;
  }

  applyTracks(state: MixerState) {
    const audible = this.resolveAudibility(state);
    for (const t of state.tracks) {
      const chain = this.audio.getTrack(t.id);
      if (!chain) continue;
      this.audio.applyFx(t.id, t.fx);
      this.audio.setTrackVolume(t.id, audible[t.id] ? t.volume : 0);
      this.audio.setTrackPan(t.id, t.pan);
      this.audio.setTrackMuted(t.id, !audible[t.id]);
    }
    this.audio.setInstrumentalMuted(!audible.__instrumental__);
    this.applyMaster(state.mix);
  }

  readMeters(state: MixerState): { tracks: Record<string, MeterReading>; instrumental: MeterReading | null; master: MeterReading | null } {
    const tracks: Record<string, MeterReading> = {};
    for (const t of state.tracks) {
      const m = this.audio.readTrack(t.id);
      if (m) tracks[t.id] = m;
    }
    return {
      tracks,
      instrumental: this.audio.readInstrumental(),
      master: this.audio.readMaster(),
    };
  }

  /**
   * Ajusta o volume das vozes automaticamente em relação ao instrumental.
   * Analisa picos e nível médio; nunca deixa passar de 0 dBFS (o limiter do
   * master cuida do resto).
   */
  autoBalance(state: MixerState): AutoBalanceReport {
    const applied: AutoBalanceReport['applied'] = [];
    const clipping: string[] = [];
    let instrumentalDb: number | null = null;

    if (state.instrumentalData) {
      const p = Math.max(peak(state.instrumentalData.left), peak(state.instrumentalData.right));
      instrumentalDb = gainToDb(p);
    }

    for (const t of state.tracks) {
      const data = state.vocalData?.[t.id];
      if (!data || !data.length) continue;
      const p = peak(data);
      if (p >= 0.999) clipping.push(t.name);
      const targetPeakDb = instrumentalDb != null ? Math.min(-1.5, instrumentalDb + 4.5) : -3.5;
      const g = autoGain(data, targetPeakDb, 14, -18);
      const beforeDb = gainToDb(t.fx.gainDb === 0 ? 1 : dbToGain(t.fx.gainDb));
      applied.push({
        trackId: t.id,
        gainDb: Math.round(20 * Math.log10(g.gain) * 10) / 10,
        fromDb: g.beforeDb,
        toDb: g.afterDb,
      });
      t.fx = { ...t.fx, gainDb: clamp(t.fx.gainDb + 20 * Math.log10(g.gain), -30, 30) };
      void beforeDb;
    }
    this.applyTracks(state);

    const vocalDb =
      applied.length && state.vocalData
        ? gainToDb(
            Math.max(
              ...Object.values(state.vocalData)
                .filter(Boolean)
                .map((d) => peak(d as Float32Array))
            )
          )
        : null;

    const balanceDb = vocalDb != null && instrumentalDb != null ? vocalDb - instrumentalDb : null;
    let message: string;
    if (!applied.length) message = 'Nada para ajustar: ainda não há voz gravada.';
    else if (balanceDb == null) message = `Volume da voz ajustado (${applied.length} faixa(s)).`;
    else if (balanceDb < -2) message = 'Voz ainda discreta em relação à base; subimos o ganho sem clipar.';
    else if (balanceDb > 9) message = 'Voz bem à frente da base — bom para destacar a letra.';
    else message = 'Voz e base equilibradas: a voz ficou um pouco à frente, sem clipping.';

    return { applied, instrumentalDb, vocalDb, balanceDb, message, clipping };
  }

  /**
   * "MELHORAR MIX AUTOMATICAMENTE": ajuste automático simples e transparente.
   * Devolve a lista do que mudou para o usuário ver.
   */
  autoImprove(state: MixerState, opts: { style?: 'studio' | 'pop' | 'rap' | 'ballad' } = {}): AutoImproveReport {
    const changes: string[] = [];
    const fx: Record<string, FxConfig> = {};
    const gains: Record<string, number> = {};

    for (const t of state.tracks) {
      const data = state.vocalData?.[t.id];
      if (!data || !data.length) continue;
      const base = defaultFx();
      const style = opts.style ?? (t.kind === 'lead' ? 'studio' : 'clean');
      const next = applyPreset(base, style as any);
      // high-pass conservador (não destrutivo: só filtra na reprodução/export)
      next.highPassOn = true;
      next.highPassHz = t.kind === 'lead' ? 95 : 120;
      // ganho automático com teto seguro
      const g = autoGain(data, -3.5, 12, -18);
      const gainDeltaDb = Math.round(20 * Math.log10(Math.max(1e-6, g.gain)) * 10) / 10;
      next.gainDb = clamp(gainDeltaDb, -18, 18);
      if (g.clipped) changes.push(`${t.name}: havia clipping na entrada — o limiter do master vai segurar.`);
      fx[t.id] = next;
      gains[t.id] = next.gainDb;
      t.fx = next;
      changes.push(`${t.name}: preset ${style.toUpperCase()}, high-pass ${next.highPassHz} Hz, ganho ${next.gainDb > 0 ? '+' : ''}${next.gainDb.toFixed(1)} dB.`);
    }

    if (state.instrumentalData) {
      const lufs = loudnessLKFS([state.instrumentalData.left, state.instrumentalData.right], state.sampleRate);
      if (isFinite(lufs)) {
        // Base muito alta? abaixa um pouco para a voz aparecer.
        const target = -14;
        const delta = clamp(target - lufs, -9, 3);
        state.mix.instrumentalVolume = clamp(dbToGain(delta) * state.mix.instrumentalVolume || 1, 0.2, 1.2);
        changes.push(`Base: loudness ≈ ${lufs.toFixed(1)} LUFS → volume ajustado ${delta > 0 ? '+' : ''}${delta.toFixed(1)} dB.`);
      }
    }

    state.mix.masterVolume = clamp(state.mix.masterVolume, 0.85, 1);
    changes.push('Master: limiter ligado em −1,2 dB (proteção contra distorção).');

    this.applyTracks(state);
    return {
      changes,
      fx,
      gains,
      master: { volume: state.mix.masterVolume, limiter: true },
      message: 'Ajuste automático simples aplicado (níveis, high-pass, compressão leve e reverb discreto).',
    };
  }

  /** Níveis medidos para o painel de análise (voz x base). */
  levels(state: MixerState): { vocalRmsDb: number | null; instrumentalRmsDb: number | null } {
    let v: number | null = null;
    for (const t of state.tracks) {
      const d = state.vocalData?.[t.id];
      if (!d || !d.length) continue;
      const db = gainToDb(rms(d));
      v = v == null ? db : Math.max(v, db);
    }
    let i: number | null = null;
    if (state.instrumentalData) {
      i = gainToDb(Math.max(rms(state.instrumentalData.left), rms(state.instrumentalData.right)));
    }
    return { vocalRmsDb: v, instrumentalRmsDb: i };
  }
}
